import { useState, useEffect } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { toast } from "sonner";
import { ListChecks, Check, Star, Lock, TrendingUp } from "lucide-react";
import GameSounds from "../lib/sounds";

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    API.get("/tasks").then(r => setTasks(r.data.tasks)).catch(() => {});
  }, []);

  const complete = async (taskId) => {
    setLoading(taskId);
    try {
      const { data } = await API.post(`/tasks/${taskId}/complete`);
      toast.success(data.message);
      GameSounds.reward();
      const res = await API.get("/tasks");
      setTasks(res.data.tasks);
    } catch (e) { toast.error(e.response?.data?.detail || "Requirement not met yet"); }
    finally { setLoading(""); }
  };

  const completed = tasks.filter((t) => t.completed);
  const available = tasks.filter((t) => !t.completed);
  const dailyAvailable = available.filter(t => t.type === "daily");
  const oneTimeAvailable = available.filter(t => t.type !== "daily");
  const dailyCompleted = completed.filter(t => t.type === "daily");
  const oneTimeCompleted = completed.filter(t => t.type !== "daily");

  return (
    <GameLayout title="Tasks & Missions">
      <div className="space-y-6" data-testid="tasks-page">
        {/* Progress overview */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-[#F8FAFC]">Mission Progress</h3>
              <p className="text-sm text-[#94A3B8]">{completed.length}/{tasks.length} missions completed</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-32 h-3 bg-[#0B0E14] rounded-full overflow-hidden border border-[#2A3241]/30">
                <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#10B981] rounded-full transition-all" style={{ width: `${tasks.length > 0 ? (completed.length / tasks.length) * 100 : 0}%` }} />
              </div>
              <span className="font-mono-game text-sm font-bold text-[#00F0FF]">{tasks.length > 0 ? Math.round((completed.length / tasks.length) * 100) : 0}%</span>
            </div>
          </div>
        </div>

        {/* Daily Tasks */}
        {(dailyAvailable.length > 0 || dailyCompleted.length > 0) && (
          <div>
            <h3 className="text-xs font-mono-game text-[#FFD600] tracking-[0.2em] uppercase mb-3">Daily Missions <span className="text-[#64748B]">(resets every 24h)</span></h3>
            <div className="space-y-3">
              {dailyAvailable.map((task) => {
                const progress = task.progress || 0;
                const reqCount = task.requirement_count || 1;
                const pct = Math.min((progress / reqCount) * 100, 100);
                const canComplete = task.can_complete;
                return (
                  <div key={task.task_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 hover:border-[#FFD600]/20 transition-all" data-testid={`task-${task.task_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${canComplete ? "bg-[#FFD600]/10 border border-[#FFD600]/30" : "bg-[#1C212E] border border-[#2A3241]"}`}>
                          {canComplete ? <Star className="w-5 h-5 text-[#FFD600]" /> : <TrendingUp className="w-5 h-5 text-[#64748B]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-bold text-[#F8FAFC]">{task.title}</h4>
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FFD600]/10 text-[#FFD600] font-bold">DAILY</span>
                          </div>
                          <p className="text-xs text-[#94A3B8] mb-3">{task.description}</p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-[#0B0E14] rounded-full overflow-hidden border border-[#2A3241]/30">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: canComplete ? "#FFD600" : "#00F0FF" }} />
                            </div>
                            <span className="font-mono-game text-xs text-[#94A3B8] whitespace-nowrap">{progress}/{reqCount}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className="font-mono-game text-sm font-bold text-[#FFD600]">+{task.reward_coins}</span>
                        {canComplete ? (
                          <button onClick={() => complete(task.task_id)} disabled={loading === task.task_id} data-testid={`complete-${task.task_id}`}
                            className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50 shadow-[0_0_10px_rgba(255,214,0,0.3)] animate-pulse-glow">
                            {loading === task.task_id ? "..." : "Claim"}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 px-3 py-1.5 bg-[#1C212E] rounded-lg">
                            <Lock className="w-3 h-3 text-[#64748B]" />
                            <span className="text-[10px] text-[#64748B] font-mono-game">IN PROGRESS</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {dailyCompleted.map((task) => (
                <div key={task.task_id} className="bg-[#131722]/50 rounded-xl border border-[#10B981]/10 p-4 flex items-center gap-4" data-testid={`task-done-${task.task_id}`}>
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center"><Check className="w-4 h-4 text-[#10B981]" /></div>
                  <div className="flex-1"><p className="text-sm text-[#94A3B8]">{task.title}</p><p className="text-[10px] text-[#64748B]">Completed today — resets tomorrow</p></div>
                  <span className="font-mono-game text-xs text-[#10B981]">+{task.reward_coins}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* One-Time Tasks */}
        {oneTimeAvailable.length > 0 && (
          <div>
            <h3 className="text-xs font-mono-game text-[#00F0FF] tracking-[0.2em] uppercase mb-3">One-Time Missions</h3>
            <div className="space-y-3">
              {oneTimeAvailable.map((task) => {
                const progress = task.progress || 0;
                const reqCount = task.requirement_count || 1;
                const pct = Math.min((progress / reqCount) * 100, 100);
                const canComplete = task.can_complete;
                return (
                  <div key={task.task_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5 hover:border-[#00F0FF]/20 transition-all" data-testid={`task-${task.task_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${canComplete ? "bg-[#00F0FF]/10 border border-[#00F0FF]/30" : "bg-[#1C212E] border border-[#2A3241]"}`}>
                          {canComplete ? <Star className="w-5 h-5 text-[#00F0FF]" /> : <TrendingUp className="w-5 h-5 text-[#64748B]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-bold text-[#F8FAFC]">{task.title}</h4>
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#00F0FF]/10 text-[#00F0FF] font-bold">ONE-TIME</span>
                          </div>
                          <p className="text-xs text-[#94A3B8] mb-3">{task.description}</p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-[#0B0E14] rounded-full overflow-hidden border border-[#2A3241]/30">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: canComplete ? "#00F0FF" : "#64748B" }} />
                            </div>
                            <span className="font-mono-game text-xs text-[#94A3B8] whitespace-nowrap">{progress}/{reqCount}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className="font-mono-game text-sm font-bold text-[#FFD600]">+{task.reward_coins}</span>
                        {canComplete ? (
                          <button onClick={() => complete(task.task_id)} disabled={loading === task.task_id} data-testid={`complete-${task.task_id}`}
                            className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 shadow-[0_0_10px_rgba(0,240,255,0.3)]">
                            {loading === task.task_id ? "..." : "Claim"}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 px-3 py-1.5 bg-[#1C212E] rounded-lg">
                            <Lock className="w-3 h-3 text-[#64748B]" />
                            <span className="text-[10px] text-[#64748B] font-mono-game">IN PROGRESS</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recently Completed One-Time (showing for 24h then disappears) */}
        {oneTimeCompleted.length > 0 && (
          <div>
            <h3 className="text-xs font-mono-game text-[#10B981] tracking-[0.2em] uppercase mb-3">Completed</h3>
            <div className="space-y-2">
              {oneTimeCompleted.map((task) => (
                <div key={task.task_id} className="bg-[#131722]/50 rounded-xl border border-[#10B981]/10 p-4 flex items-center gap-4 opacity-60" data-testid={`task-done-${task.task_id}`}>
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center"><Check className="w-4 h-4 text-[#10B981]" /></div>
                  <div className="flex-1"><p className="text-sm text-[#94A3B8] line-through">{task.title}</p></div>
                  <span className="font-mono-game text-xs text-[#10B981]">+{task.reward_coins}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tasks.length === 0 && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-12 text-center">
            <ListChecks className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#94A3B8]">No missions available</p>
          </div>
        )}
      </div>
    </GameLayout>
  );
}
